import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { authService, type AuthUser } from '@/services/authService'

interface AuthContextType {
  currentUser: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  /** True when the session check failed because the server was unreachable (network/5xx), not because the user is signed out. */
  connectionLost: boolean
  retrySession: () => void
  login: () => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [connectionLost, setConnectionLost] = useState(false)

  const checkSession = useCallback((forceReload = false) => {
    setIsLoading(true)
    // Initial load shares the cached fetch with the route guards (a single /auth/me); a manual
    // retry or a fresh sign-in forces a new fetch and replaces the cache.
    const session = forceReload ? authService.reloadSession() : authService.ensureSession()
    session.then((result) => {
      console.log('[AuthContext] Session check result:', result.status)
      if (result.status === 'authenticated') {
        setCurrentUser(result.user)
        setIsAuthenticated(true)
        setConnectionLost(false)
      } else if (result.status === 'unreachable') {
        // Keep whatever user we had; the server is down, the session may still be valid.
        setConnectionLost(true)
      } else {
        setCurrentUser(null)
        setIsAuthenticated(false)
        setConnectionLost(false)
      }
      setIsLoading(false)
    })
  }, [])

  useEffect(() => {
    checkSession()
  }, [checkSession])

  const retrySession = useCallback(() => {
    checkSession(true)
  }, [checkSession])

  const login = () => {
    checkSession(true)
  }

  const logout = async () => {
    setCurrentUser(null)
    setIsAuthenticated(false)
    authService.clearCachedSession()
    authService.logout()
  }

  return (
    <AuthContext.Provider value={{ currentUser, isAuthenticated, isLoading, connectionLost, retrySession, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
